import { buildIncomeProjection } from '../incomeProjection.js';
import { BUDGET_YEAR_OFFSET, addOneTimeLines, applyBudget, } from './budget.js';
import { budgetYearInputs } from './budgetYear.js';
import { solveBudgetYear, } from './reserve.js';
import { budgetSnapshot } from './snapshot.js';
import { buildProjectionModel } from './viewModel.js';
const earliest = (dates) => dates.every((d) => d !== undefined) ? [...dates].sort()[0] : undefined;
const latest = (dates) => dates.every((d) => d !== undefined) ? [...dates].sort().at(-1) : undefined;
function termOfAll(terms, collectingMonths) {
    const starts = terms.map((t) => (t.kind === 'once' ? t.on : t.from));
    const ends = terms.map((t) => (t.kind === 'once' ? t.on : t.until));
    if (terms.every((t) => t.kind === 'once') && new Set(starts).size === 1) {
        return { kind: 'once', on: starts[0] };
    }
    const [first, ...rest] = collectingMonths;
    if (terms.every((t) => t.kind === 'monthly') &&
        first !== undefined &&
        rest.every((amount) => Math.abs(amount - first) < 0.005)) {
        return { kind: 'monthly', perMonth: first, from: earliest(starts), until: latest(ends) };
    }
    return { kind: 'repeating', from: earliest(starts), until: latest(ends) };
}
export function fundCollectionsOf(pnl, offset) {
    const firstYear = pnl.periods[0]?.fiscalYear;
    if (firstYear === undefined)
        return [];
    const inYear = pnl.periods.flatMap((p, i) => (p.fiscalYear === firstYear + offset ? [i] : []));
    return pnl.fundSources
        .flatMap((source) => {
        const collecting = source.schedules.filter((s) => inYear.some((i) => (s.scheduled[i] ?? 0) !== 0));
        const monthTotals = inYear.map((i) => collecting.reduce((sum, s) => sum + (s.scheduled[i] ?? 0), 0));
        const collections = monthTotals.reduce((sum, amount) => sum + amount, 0);
        if (collections <= 0)
            return [];
        return [
            {
                line: source.key.subcategory,
                name: source.name,
                scheduleCount: collecting.length,
                collections,
                term: termOfAll(collecting.map((s) => s.term), monthTotals.filter((amount) => amount !== 0)),
            },
        ];
    })
        .sort((a, b) => b.collections - a.collections);
}
export function budgetPlanModel(pnl, settings) {
    const rate = settings.cpiPercent / 100;
    const projected = buildProjectionModel(pnl, { expenseInflationRate: rate });
    const budget = addOneTimeLines(applyBudget(projected, settings.edits, rate), settings.oneTimeLines);
    const scope = { kind: settings.kind, edits: settings.edits };
    const inputs = budgetYearInputs(budget, {
        ...scope,
        units: settings.units,
        projected,
        today: settings.today,
    });
    const result = solveBudgetYear(inputs, settings.reserve);
    const snapshot = budgetSnapshot(budget, result, settings.oneTimeLines, settings.chargesFallback, scope);
    const funds = fundCollectionsOf(pnl, BUDGET_YEAR_OFFSET);
    return { projected, budget, inputs, result, snapshot, funds };
}
export function computeBudgetPlan(projection, income, settings) {
    return budgetPlanModel(buildIncomeProjection(projection, income), settings);
}
//# sourceMappingURL=budgetPlan.js.map